import {
  type ReadError,
  type ReadMeasurements,
  readMeasurements,
  styleOfTexts,
  type UnitSetup,
} from "@/lib/tool-kit/geometry/measurements";
import {
  computeSolid,
  SOLID_FIELDS,
  type SolidError,
  type SolidFieldId,
  type SolidId,
  type SolidResult,
} from "@/lib/tool-kit/geometry/solids";
import type { ResultFormat } from "@/lib/tool-kit/geometry/working";

/** What each solid starts with, so the page opens on a worked example. */
export const SOLID_DEFAULTS: Record<SolidId, Record<string, string>> = {
  cube: { side: "4" },
  box: { length: "6", width: "4", height: "3" },
  triPrism: { sideA: "3", sideB: "4", sideC: "5", prismLength: "10" },
  cylinder: { radius: "3", height: "5" },
  cone: { radius: "3", height: "4" },
  sphere: { radius: "3" },
  hemisphere: { radius: "3" },
  pyramid: { length: "6", width: "6", height: "4" },
  frustum: { bottomRadius: "4", topRadius: "2", height: "3" },
  ellipsoid: { semiA: "3", semiB: "2", semiC: "1" },
};

export type SolidOutcome =
  | {
      ok: true;
      result: SolidResult;
      read: ReadMeasurements;
      format: ResultFormat;
    }
  | { ok: false; error: ReadError | SolidError };

/** Read the typed measurements, convert them to the answer unit and compute the solid. */
export function evaluateSolid(
  solid: SolidId,
  texts: Record<string, string>,
  setup: UnitSetup,
  mark: "." | ","
): SolidOutcome {
  const ids: readonly SolidFieldId[] = SOLID_FIELDS[solid];
  const style = styleOfTexts(ids.map((id) => texts[id] ?? ""));
  const read = readMeasurements(ids, texts, setup, mark, style);
  if (!read.ok) {
    return read;
  }
  const format: ResultFormat = { mark, style };
  const result = computeSolid(solid, read.lengths, format);
  if (!result.ok) {
    return result;
  }
  return { ok: true, result, read, format };
}
